import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { blogService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

const Blogs = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    
    // API state
    const [posts, setPosts] = useState([]);
    const [sliderPosts, setSliderPosts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [previousPage, setPreviousPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    
    const limit = 6;
    const searchTimeout = useRef(null);
    

    useEffect(() => {
        fetchPosts(0);
        fetchSliderPosts(); // Slider için antecedence=true olanları çek
    }, [language]);

    // Slider için antecedence=true olan postları çek
    const fetchSliderPosts = async () => {
        try {
            const data = await blogService.getPosts(10, 0, true); // antecedence=true
            setSliderPosts(data.results || []);
        } catch (err) {
            console.error('Slider posts error:', err);
        }
    };

    // Search için debounce
    useEffect(() => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            if (searchQuery.trim()) {
                setIsSearching(true);
                searchPosts(0);
            } else {
                setIsSearching(false);
                fetchPosts(0);
            }
        }, 500);

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [searchQuery, language]);

    const fetchPosts = async (offset) => {
        setLoading(true);
        setError(null);
        try {
            const data = await blogService.getPosts(limit, offset);
            setPosts(data.results || []);
            setTotalCount(data.count || 0);
            setNextPage(data.next);
            setPreviousPage(data.previous);
        } catch (err) {
            console.error('Blog API error:', err);
            setError(language === 'fa' ? 'خطا در دریافت مقالات' : 'Error fetching posts');
        } finally {
            setLoading(false);
        }
    };

    // Arama fonksiyonu - client-side filtreleme
    const searchPosts = (offset) => {
        setLoading(true);
        setError(null);
        
        blogService.getPosts(100, 0).then(data => {
            const allPosts = data.results || [];
            const query = searchQuery.toLowerCase().trim();
            
            const filtered = allPosts.filter(post => {
                const title = (post.title || getTranslation(post.translations, 'title') || '').toLowerCase();
                const summary = (post.summary || getTranslation(post.translations, 'summary') || '').toLowerCase();
                return title.includes(query) || summary.includes(query);
            });
            
            setSliderPosts([]);
            setPosts(filtered);
            setTotalCount(filtered.length);
            setNextPage(null);
            setPreviousPage(null);
            setLoading(false);
        }).catch(err => {
            console.error('Search error:', err);
            setError(language === 'fa' ? 'خطا در جستجو' : 'Search error');
            setLoading(false);
        });
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        if (nextPage && !isSearching) {
            const nextOffset = currentPage * limit;
            setCurrentPage(prev => prev + 1);
            fetchPosts(nextOffset);
        }
    };

    const handlePreviousPage = () => {
        if (previousPage && !isSearching) {
            const prevOffset = (currentPage - 2) * limit;
            setCurrentPage(prev => prev - 1);
            fetchPosts(prevOffset);
        }
    };

    const handlePostClick = (slug) => {
        navigate(`/blog/${slug}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getTranslation = (obj, field) => {
        if (!obj) return '';
        if (obj[language]?.[field]) return obj[language][field];
        const codes = Object.keys(obj);
        if (codes.length > 0 && obj[codes[0]]?.[field]) return obj[codes[0]][field];
        return '';
    };

    // Loading
    if (loading && posts.length === 0 && !isSearching) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-[#1F4284] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#1F4284] font-bold text-lg">
                            {language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-red-600 font-bold text-lg">{error}</p>
                        <button onClick={() => { setIsSearching(false); fetchPosts(0); }} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                            {language === 'fa' ? 'تلاش مجدد' : 'Try Again'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden">
            {/* Search Input */}
            <motion.div 
                className="flex justify-end mb-4 lg:mb-8"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="relative w-full sm:w-[342px] max-w-md">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#1F4284] rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearch}
                        placeholder={language === 'fa' ? 'جستجو...' : 'Search...'}
                        className="w-full pr-4 pl-12 sm:pl-14 py-2 sm:py-3 rounded-full border-2 border-[#EBEBEB] focus:border-[#1F4284] focus:outline-none transition-colors text-[#204286] font-medium text-xs sm:text-sm placeholder:text-gray-400"
                    />
                </div>
            </motion.div>

            {/* Slider - antecedence=true olan postlar */}
            {!isSearching && sliderPosts.length > 0 && (
                <motion.div 
                    className="relative mb-8 lg:mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <Swiper
                        modules={[Navigation, Autoplay]}
                        slidesPerView={1}
                        spaceBetween={0}
                        loop={true}
                        autoplay={{ delay: 4000, disableOnInteraction: false }}
                        navigation={{
                            prevEl: '.blog-swiper-prev',
                            nextEl: '.blog-swiper-next',
                        }}
                        breakpoints={{
                            320: { slidesPerView: 2, spaceBetween: 8 },
                            480: { slidesPerView: 2, spaceBetween: 10 },
                            640: { slidesPerView: 2, spaceBetween: 12 },
                            1024: { slidesPerView: 1, spaceBetween: 0 }
                        }}
                        className="blogSwiper"
                    >
                        {sliderPosts.map((slide) => (
                            <SwiperSlide key={slide.slug}>
                                <div 
                                    className="hidden lg:flex items-center rounded-xl overflow-hidden cursor-pointer"
                                    style={{ backgroundColor: '#EFEFFF', maxWidth: '1010px', height: '280px', margin: '0 auto' }}
                                    onClick={() => handlePostClick(slide.slug)}
                                >
                                    <div className="w-[370px] h-[237px] bg-white rounded-lg mr-6 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                        {slide.banner ? (
                                            <img src={slide.banner} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-gray-400 text-sm">{language === 'fa' ? 'تصویر' : 'Image'}</span>
                                        )}
                                    </div>
                                    <div className="flex-1 px-6 text-right">
                                        <h2 className="text-[32px] font-extrabold text-[#003373] mb-3 leading-tight line-clamp-2">
                                            {slide.title || getTranslation(slide.translations, 'title')}
                                        </h2>
                                        <p className="text-lg font-normal text-[#003373] mb-3">{formatDate(slide.created_at)}</p>
                                        <p className="text-lg font-medium text-[#003373]/80 leading-relaxed line-clamp-2">
                                            {slide.summary || getTranslation(slide.translations, 'summary')}
                                        </p>
                                    </div>
                                </div>
                                <div 
                                    className="flex lg:hidden flex-col rounded-xl overflow-hidden p-2"
                                    style={{ backgroundColor: '#EFEFFF', height: '100%' }}
                                    onClick={() => handlePostClick(slide.slug)}
                                >
                                    <div className="w-full h-24 sm:h-32 bg-white rounded-lg mb-1.5 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                        {slide.banner ? (
                                            <img src={slide.banner} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-gray-400 text-[9px] sm:text-xs">{language === 'fa' ? 'تصویر' : 'Image'}</span>
                                        )}
                                    </div>
                                    <div className="flex-1 text-right">
                                        <h2 className="text-[10px] sm:text-xs font-extrabold text-[#003373] mb-0.5 leading-tight line-clamp-2">
                                            {slide.title || getTranslation(slide.translations, 'title')}
                                        </h2>
                                        <p className="text-[9px] sm:text-[10px] font-normal text-[#003373] mb-1">{formatDate(slide.created_at)}</p>
                                        <p className="text-[9px] sm:text-[10px] font-normal text-[#003373]/70 leading-relaxed line-clamp-2">
                                            {slide.summary || getTranslation(slide.translations, 'summary')}
                                        </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <button className="blog-swiper-prev absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 sm:-translate-x-4 z-10 w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all">
                        <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-[#1F4284]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button className="blog-swiper-next absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 sm:translate-x-4 z-10 w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all">
                        <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-[#1F4284]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </motion.div>
            )}

            {/* Arama sonuç başlığı */}
            {isSearching && (
                <div className="mb-6 text-center">
                    <p className="text-[#204286] font-bold text-lg">
                        {language === 'fa' ? `نتایج جستجو برای "${searchQuery}"` : `Search results for "${searchQuery}"`}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                        {totalCount} {language === 'fa' ? 'نتیجه' : 'result'}{totalCount !== 1 ? (language === 'fa' ? '' : 's') : ''}
                    </p>
                </div>
            )}

            {/* Blog Listesi */}
            <div className="max-w-[1010px] mx-auto">
                {posts.map((post, index) => (
                    <motion.div
                        key={post.slug}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                        <div className="flex items-center py-3 sm:py-4 lg:py-6 cursor-pointer" onClick={() => handlePostClick(post.slug)}>
                            <div 
                                className="w-[100px] h-[65px] sm:w-[200px] sm:h-[140px] lg:w-[328px] lg:h-[200px] rounded-lg flex-shrink-0 ml-2 sm:ml-4 lg:ml-6 flex items-center justify-center overflow-hidden"
                                style={{ backgroundColor: '#EFEFFF' }}
                            >
                                {post.banner ? (
                                    <img src={post.banner} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-gray-400 text-[10px] sm:text-sm">{language === 'fa' ? 'تصویر' : 'Image'}</span>
                                )}
                            </div>
                            <div className="flex-1 text-right min-w-0">
                                <h2 className="text-xs sm:text-xl lg:text-[32px] font-extrabold text-[#003373] mb-0.5 sm:mb-2 leading-tight line-clamp-2 lg:line-clamp-none">
                                    {post.title || getTranslation(post.translations, 'title')}
                                </h2>
                                <p className="text-[10px] sm:text-sm lg:text-lg font-normal text-[#003373] mb-0.5 sm:mb-2">
                                    {formatDate(post.created_at)}
                                </p>
                                <p className="text-[10px] sm:text-sm lg:text-lg font-normal text-[#003373]/70 leading-relaxed line-clamp-2 sm:line-clamp-3 lg:line-clamp-2">
                                    {post.summary || getTranslation(post.translations, 'summary')}
                                </p>
                            </div>
                        </div>
                        {index < posts.length - 1 && (
                            <div className="border-b border-black/20" />
                        )}
                    </motion.div>
                ))}

                {posts.length === 0 && !loading && (
                    <p className="text-center text-gray-500 py-10">
                        {isSearching 
                            ? (language === 'fa' ? 'نتیجه‌ای یافت نشد' : 'No results found')
                            : (language === 'fa' ? 'مقاله‌ای یافت نشد' : 'No posts found')
                        }
                    </p>
                )}
            </div>

            {/* Pagination - Sadece arama yapılmadığında */}
            {!isSearching && totalPages > 1 && (
                <div className="flex justify-center items-center gap-1 sm:gap-2 mt-6 lg:mt-12">
                    <button
                        onClick={handlePreviousPage}
                        disabled={!previousPage}
                        className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg rotate-180 flex items-center justify-center transition-all ${
                            !previousPage ? 'text-gray-300 cursor-not-allowed' : 'text-[#FFB947] hover:bg-[#FFF3E0]'
                        }`}
                    >
                        <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        const showPage = pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - currentPage) <= 1;
                        if (!showPage) {
                            if (pageNum === 2 && currentPage > 3) return <span key="dots1" className="text-[#204286] px-0.5 text-xs">...</span>;
                            if (pageNum === totalPages - 1 && currentPage < totalPages - 2) return <span key="dots2" className="text-[#204286] px-0.5 text-xs">...</span>;
                            return null;
                        }
                        return (
                            <button
                                key={index}
                                onClick={() => {
                                    setCurrentPage(pageNum);
                                    fetchPosts((pageNum - 1) * limit);
                                }}
                                className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                                    currentPage === pageNum ? 'text-[#FFB947]' : 'text-[#204286] hover:text-[#FFB947]'
                                }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}

                    <button
                        onClick={handleNextPage}
                        disabled={!nextPage}
                        className={`w-7 h-7 sm:w-10 sm:h-10 rotate-180 rounded-lg flex items-center justify-center transition-all ${
                            !nextPage ? 'text-gray-300 cursor-not-allowed' : 'text-[#FFB947] hover:bg-[#FFF3E0]'
                        }`}
                    >
                        <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Blogs;